import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import { copyTextToClipboard } from '../utils/clipboard';
import './SystemLogsPage.css';

const TOOL_KINDS = [
  '',
  'search',
  'filter',
  'http_request',
  'webpage_scrape',
  'entry_update',
  'entry_create',
  'tool_chain',
  'analytics',
  'web_search',
  'mcp_call',
  'research',
  'email_draft',
];

const SOURCES = ['', 'chat', 'scheduler', 'tool_chain', 'unknown'];

function defaultFrom() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function defaultTo() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return iso;
  }
}

function statusLabel(row) {
  if (row?.success === false) return 'error';
  if (row?.success) return 'ok';
  return '—';
}

function outputPayload(detail) {
  if (!detail || detail.error) return null;
  return {
    id: detail.id,
    correlation_id: detail.correlation_id,
    parent_id: detail.parent_id,
    suffix: detail.suffix,
    session_id: detail.session_id,
    args_preview: detail.args_preview,
    result_preview: detail.result_preview,
  };
}

function formatLogClipboardText(row, detail, { includeUser = false } = {}) {
  const headers = ['Id', 'Time', 'Time UTC', ...(includeUser ? ['User'] : []), 'Source', 'Type', 'Tool', 'Status', 'Duration', 'Summary'];
  const values = [
    row.id || '—',
    formatWhen(row.created_at),
    row.created_at || '—',
    ...(includeUser ? [row.user_email || row.user_id || '—'] : []),
    row.source || '—',
    row.tool_kind || '—',
    row.tool_slug || '—',
    statusLabel(row),
    row.duration_ms != null ? `${row.duration_ms} ms` : '—',
    row.error || row.summary || '—',
  ];
  const lines = [headers.join('\t'), values.join('\t')];
  const output = outputPayload(detail);
  if (output) {
    lines.push('', 'Output', JSON.stringify(output, null, 2));
  } else if (detail?.error) {
    lines.push('', 'Output', String(detail.error));
  }
  return lines.join('\n');
}

function SystemLogsPage() {
  const { user } = useAuth();
  const isSuperAdmin = String(user?.accountType || '').toLowerCase() === 'superadmin';
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [q, setQ] = useState('');
  const [toolKind, setToolKind] = useState('');
  const [source, setSource] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (from) {
      const d = new Date(from);
      if (!Number.isNaN(d.getTime())) p.set('from', d.toISOString());
    }
    if (to) {
      const d = new Date(to);
      if (!Number.isNaN(d.getTime())) p.set('to', d.toISOString());
    }
    if (q.trim()) p.set('q', q.trim());
    if (toolKind) p.set('tool_kind', toolKind);
    if (source) p.set('source', source);
    if (isSuperAdmin && userFilter.trim()) {
      if (userFilter.includes('@')) p.set('user_email', userFilter.trim());
      else p.set('user_id', userFilter.trim());
    }
    p.set('limit', '100');
    return p.toString();
  }, [from, to, q, toolKind, source, userFilter, isSuperAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${API_BASE_URL}/casc/v1/api/system-logs?${queryString}`, { headers });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || json.success === false) {
        throw new Error(json.error || `Request failed (${resp.status})`);
      }
      setRows(Array.isArray(json.rows) ? json.rows : []);
      setTotal(Number(json.total) || 0);
    } catch (err) {
      setError(err.message || String(err));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    load();
  }, [load]);

  const fetchLogDetail = async (id) => {
    const headers = await getAuthHeaders();
    const resp = await fetch(`${API_BASE_URL}/casc/v1/api/system-logs/${id}`, { headers });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok || !json.data) throw new Error(json.error || 'Failed to load log');
    return json.data;
  };

  const openDetail = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(id);
    setDetail(null);
    try {
      setDetail(await fetchLogDetail(id));
    } catch (err) {
      setDetail({ error: err.message || String(err) });
    }
  };

  const copyLogEntry = async (event, row) => {
    event.stopPropagation();
    try {
      let payload = expandedId === row.id && detail && !detail.error ? detail : null;
      if (!payload) payload = await fetchLogDetail(row.id);
      const ok = await copyTextToClipboard(formatLogClipboardText(row, payload, { includeUser: isSuperAdmin }));
      if (ok) {
        setCopiedId(row.id);
        window.setTimeout(() => setCopiedId((cur) => (cur === row.id ? null : cur)), 1500);
      }
    } catch (err) {
      setError(err.message || String(err));
    }
  };

  return (
    <div className="system-logs-page">
      <h1>
        <i className="fas fa-clipboard-list" aria-hidden /> System Logs
      </h1>
      <p className="system-logs-lead">
        Tool calls from chat, schedulers, and tool chains.
        {isSuperAdmin
          ? ' You can see every user’s logs.'
          : ' You only see your own logs.'}
      </p>

      <form
        className="system-logs-filters"
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
      >
        <label>
          From
          <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          To
          <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label>
          Keyword
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="id, slug, error, summary…"
          />
        </label>
        <label>
          Tool type
          <select value={toolKind} onChange={(e) => setToolKind(e.target.value)}>
            {TOOL_KINDS.map((k) => (
              <option key={k || 'all'} value={k}>{k || 'All types'}</option>
            ))}
          </select>
        </label>
        <label>
          Source
          <select value={source} onChange={(e) => setSource(e.target.value)}>
            {SOURCES.map((s) => (
              <option key={s || 'all'} value={s}>
                {s === 'scheduler' ? 'scheduler (cron / Run now)' : (s || 'All sources')}
              </option>
            ))}
          </select>
        </label>
        {isSuperAdmin && (
          <label>
            User
            <input
              type="text"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              placeholder="email or user id"
            />
          </label>
        )}
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Loading…' : 'Apply filters'}
        </button>
      </form>

      {error && (
        <div className="alert alert-error">
          <i className="fas fa-exclamation-circle" /> {error}
        </div>
      )}

      <div className="system-logs-meta">{total} matching call{total === 1 ? '' : 's'}</div>

      <div className="system-logs-table-wrap">
        <table className="system-logs-table">
          <thead>
            <tr>
              <th>Id</th>
              <th>Time</th>
              {isSuperAdmin && <th>User</th>}
              <th>Source</th>
              <th>Type</th>
              <th>Tool</th>
              <th>Status</th>
              <th>Duration</th>
              <th>Summary</th>
              <th className="system-logs-copy-col" aria-label="Copy" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={isSuperAdmin ? 10 : 9} className="system-logs-empty">
                  No tool calls in this range.
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <React.Fragment key={row.id}>
                <tr
                  className={`system-logs-row${row.success === false ? ' is-error' : ''}${expandedId === row.id ? ' is-open' : ''}`}
                  onClick={() => openDetail(row.id)}
                >
                  <td className="system-logs-id">
                    <code title={row.id}>{row.id || '—'}</code>
                  </td>
                  <td title={row.created_at || ''}>{formatWhen(row.created_at)}</td>
                  {isSuperAdmin && (
                    <td className="system-logs-user">{row.user_email || row.user_id || '—'}</td>
                  )}
                  <td>{row.source || '—'}</td>
                  <td>{row.tool_kind || '—'}</td>
                  <td>
                    <code>{row.tool_slug || '—'}</code>
                    {row.parent_id ? <span className="system-logs-chain" title="Part of a tool chain"> chain</span> : null}
                  </td>
                  <td>{row.success === false ? 'error' : (row.success ? 'ok' : '—')}</td>
                  <td>{row.duration_ms != null ? `${row.duration_ms} ms` : '—'}</td>
                  <td className="system-logs-summary">{row.error || row.summary || '—'}</td>
                  <td className="system-logs-copy-col">
                    <button
                      type="button"
                      className="system-logs-copy-btn"
                      title={copiedId === row.id ? 'Copied' : 'Copy log entry'}
                      aria-label={copiedId === row.id ? 'Copied' : 'Copy log entry'}
                      onClick={(e) => copyLogEntry(e, row)}
                    >
                      <i className={`fas ${copiedId === row.id ? 'fa-check' : 'fa-copy'}`} aria-hidden />
                    </button>
                  </td>
                </tr>
                {expandedId === row.id && (
                  <tr className="system-logs-detail-row">
                    <td colSpan={isSuperAdmin ? 10 : 9}>
                      {!detail && <div className="text-muted">Loading…</div>}
                      {detail?.error && <div className="alert alert-error">{detail.error}</div>}
                      {detail && !detail.error && (
                        <pre className="system-logs-json">{JSON.stringify(outputPayload(detail), null, 2)}</pre>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SystemLogsPage;
